import React, { useEffect, useRef, useState } from 'react';
import {
  Camera, Loader2, Check, User as UserIcon, Mail, MapPin, Lock, Save, Volume2, VolumeX, Speaker, Sun, Moon, Home as HomeIcon, Mic, Highlighter, Bell,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { updateProfile, changePassword, uploadAvatar, checkHandleAvailable, getImmersiveSettings, updateImmersiveSettings } from '../lib/api';
import { broadcastPreferenceUpdate } from '../lib/preferencesChannel';
import VoiceGenderPicker from '../components/VoiceGenderPicker';
import SanctuaryModerationDialog, { parseSanctuaryError } from '../components/SanctuaryModerationDialog';

// Small text "@" glyph used as the input icon for the handle field so it
// visually reads as an @-username without pulling in another lucide icon.
const AT_ICON = ({ size = 18, className = '' }) => (
  <span className={`inline-flex items-center justify-center text-lg font-semibold ${className}`} style={{ width: size, height: size, lineHeight: 1 }}>@</span>
);

// Same rules as the backend: lowercase letters, digits, underscore; 3-30 chars.
const HANDLE_RE = /^[a-z0-9_]{3,30}$/;
const normalizeHandle = (raw) => (raw || '')
  .replace(/^@+/, '')
  .toLowerCase()
  .replace(/[^a-z0-9_]/g, '')
  .slice(0, 30);

// Faith tradition picker — simplified binary choice ("Christian" vs
// "Non-Christian") that steers the Prayer Assistance system prompt.
// The old multi-select religion-filter UI (display_religions) was
// retired alongside this simplification.
const SHOW_RELIGION_UI = true;

const Section = ({ title, subtitle, children }) => (
  <section className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-5 sm:p-6">
    <div className="mb-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    {label && <div className="text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">{label}</div>}
    {children}
    {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
  </label>
);

const InputRow = ({ icon: Icon, ...props }) => (
  <div className="flex items-center h-11 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 focus-within:border-amber-500 dark:focus-within:border-amber-400 overflow-hidden">
    {Icon && <Icon size={18} className="ml-3 text-zinc-400 flex-shrink-0" />}
    <input
      {...props}
      className="flex-1 bg-transparent outline-none px-3 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400 disabled:cursor-not-allowed disabled:opacity-70"
    />
  </div>
);

const Toast = ({ text, tone = 'success' }) => (
  <div
    className={`text-sm rounded-lg px-3 py-2 border ${
      tone === 'success'
        ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900'
        : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
    }`}
    data-testid={`settings-toast-${tone}`}
  >
    {text}
  </div>
);

const Avatar = ({ user, previewUrl }) => {
  const initial = (user?.name || user?.email || 'U').trim()[0]?.toUpperCase();
  const src = previewUrl || (user?.picture ? user.picture : null);
  if (src) {
    return <img src={src} alt={user?.name || 'You'} className="w-full h-full object-cover" />;
  }
  return (
    <span className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white text-2xl font-semibold">
      {initial}
    </span>
  );
};

// `view` controls which sections render:
//   - 'profile'  → identity + password (bound to /profile route)
//   - 'settings' → display, narration, nearby radius (bound to /settings)
//   - anything else / undefined → render everything (legacy behavior).
// All state, effects, and handlers are always instantiated so we don't
// have to duplicate the ~1200-line component into two files.
const Settings = ({ view }) => {
  const { user, loading: authLoading, refresh, openAuthModal } = useAuth();
  const { theme, setTheme } = useTheme();

  // Profile form. Every input uses a LAZY INITIALIZER seeded from the
  // already-loaded `user` object so a fresh mount (page reload) renders
  // with the persisted values immediately — no momentary "system_fallback"
  // flash and no risk of the local default winning over the stored value.
  const [name, setName] = useState(() => user?.name || '');
  const [zip, setZip] = useState(() => user?.zip_code || '');
  // Bio / mission statement — free-form intro shown prominently on
  // the author's Circle page. Optional, capped at 280 chars to keep
  // the Circle header layout stable.
  const [bio, setBio] = useState(() => user?.bio || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null); // {tone, text}
  // Sanctuary Moderation block (handle change or avatar upload).
  const [sanctuary, setSanctuary] = useState(null);

  // Narration preference — read directly from the profile. `useState` with
  // a function initialiser only runs once on mount, so a page reload picks
  // up the stored value on the first render (assuming AuthContext hydrated
  // before this component mounted, which it does — Settings is gated on
  // `!authLoading && user` below). The follow-up useEffect below re-syncs
  // if the field changes later (e.g., updated in another tab).
  const [narration, setNarration] = useState(() => (
    ['author_only', 'system_fallback', 'silent_highlight', 'silent'].includes(user?.narration_preference)
      ? user.narration_preference
      : 'silent'
  ));
  const [savingNarration, setSavingNarration] = useState(false);
  const [narrationMsg, setNarrationMsg] = useState(null);
  // Voice gender is only relevant when narration === 'system_fallback'.
  const [voiceGender, setVoiceGender] = useState(() => user?.system_voice_gender || 'female');
  const [savingVoiceGender, setSavingVoiceGender] = useState(false);

  // Default home tab
  const [defaultTab, setDefaultTab] = useState(() => user?.default_home_tab || 'All');
  const [savingTab, setSavingTab] = useState(false);
  const [tabMsg, setTabMsg] = useState(null);

  // Nearby radius (miles) — one of 10 / 25 / 50 / 100. Defaults to 50 for
  // new accounts and any legacy user without a stored value.
  const [nearbyRadius, setNearbyRadius] = useState(() => (
    [10, 25, 50, 100].includes(user?.nearby_radius_mi) ? user.nearby_radius_mi : 25
  ));
  const [savingRadius, setSavingRadius] = useState(false);
  const [radiusMsg, setRadiusMsg] = useState(null);

  // "Show stats on my Circle page" — off by default (opt-in). When
  // enabled, the CirclePage renders a 3-card grid (prayers, total
  // prayed, followers) but ONLY to the author themselves. Mirrors the
  // notification-prefs pattern: local state hydrates from `user`, an
  // optimistic flip fires the PATCH, and a "touched" ref stops
  // AuthContext refreshes from stomping the value mid-save.
  const [showCircleStats, setShowCircleStats] = useState(Boolean(user?.show_circle_stats));
  const [savingCircleStats, setSavingCircleStats] = useState(false);
  const [circleStatsMsg, setCircleStatsMsg] = useState(null);
  const showCircleStatsTouchedRef = useRef(false);
  useEffect(() => {
    if (showCircleStatsTouchedRef.current) return;
    setShowCircleStats(Boolean(user?.show_circle_stats));
  }, [user?.show_circle_stats]);
  const onToggleCircleStats = async (checked) => {
    showCircleStatsTouchedRef.current = true;
    setSavingCircleStats(true);
    setCircleStatsMsg(null);
    setShowCircleStats(checked);
    try {
      await updateProfile({ show_circle_stats: checked });
      setCircleStatsMsg({ tone: 'success', text: 'Saved' });
      if (typeof refresh === 'function') {
        try { await refresh(); } catch (err) { console.warn('refresh after circle-stats toggle failed', err); }
      }
    } catch (err) {
      console.error('save show_circle_stats failed', err);
      setShowCircleStats(!checked);
      setCircleStatsMsg({ tone: 'error', text: err?.response?.data?.detail || 'Could not save — try again' });
    } finally {
      setSavingCircleStats(false);
    }
  };

  // Notification preferences — three in-app toggles that steer
  // whether direct_notifications rows get written when the matching
  // event fires. Missing keys default to True (opt-out model) so
  // legacy accounts see every type until they explicitly turn one off.
  const [notifPrefs, setNotifPrefs] = useState(() => ({
    inapp_prayer_watched: (user?.notification_prefs?.inapp_prayer_watched ?? true),
    inapp_random_share: (user?.notification_prefs?.inapp_random_share ?? true),
    inapp_thanks_reply: (user?.notification_prefs?.inapp_thanks_reply ?? true),
  }));
  const [savingNotif, setSavingNotif] = useState(null); // key currently saving
  const [notifMsg, setNotifMsg] = useState(null);
  // Track whether the user has already interacted with the toggles so
  // an in-flight AuthContext refresh doesn't stomp the local value
  // mid-save (mirrors the pattern used for the religion filter).
  const notifPrefsTouchedRef = useRef(false);
  useEffect(() => {
    if (notifPrefsTouchedRef.current) return;
    setNotifPrefs({
      inapp_prayer_watched: (user?.notification_prefs?.inapp_prayer_watched ?? true),
      inapp_random_share: (user?.notification_prefs?.inapp_random_share ?? true),
      inapp_thanks_reply: (user?.notification_prefs?.inapp_thanks_reply ?? true),
    });
  }, [user?.notification_prefs?.inapp_prayer_watched, user?.notification_prefs?.inapp_random_share, user?.notification_prefs?.inapp_thanks_reply]);
  const onToggleNotif = async (key, checked) => {
    notifPrefsTouchedRef.current = true;
    setSavingNotif(key);
    setNotifMsg(null);
    setNotifPrefs((prev) => ({ ...prev, [key]: checked }));
    try {
      await updateProfile({ notification_prefs: { [key]: checked } });
      setNotifMsg({ tone: 'success', text: 'Saved' });
      if (typeof refresh === 'function') {
        try { await refresh(); } catch (err) { console.warn('refresh after notif toggle failed', err); }
      }
    } catch (err) {
      console.error('save notif pref failed', err);
      // Roll back the optimistic flip on failure.
      setNotifPrefs((prev) => ({ ...prev, [key]: !checked }));
      setNotifMsg({ tone: 'error', text: err?.response?.data?.detail || 'Could not save — try again' });
    } finally {
      setSavingNotif(null);
    }
  };

  // Gender — used by the Prayer assistance to pick pronouns in drafts about
  // the user themselves. "unspecified" is the safe default and keeps
  // drafts name-only (no gendered pronouns).
  const [gender, setGender] = useState(() => (
    ['female', 'male', 'unspecified'].includes(user?.gender) ? user.gender : 'unspecified'
  ));
  const [savingGender, setSavingGender] = useState(false);
  const [genderMsg, setGenderMsg] = useState(null);

  // Faith tradition — steers the Prayer Assistance system prompt.
  // Simplified binary: "christian" (default) writes prayers in a
  // Christian voice; "non_christian" keeps the guide neutral.
  const RELIGION_OPTIONS = [
    { value: 'christian', label: 'Christian' },
    { value: 'non_christian', label: 'Non-Christian' },
  ];
  const RELIGION_VALUES = RELIGION_OPTIONS.map((o) => o.value);
  const [religion, setReligion] = useState(() => (
    RELIGION_VALUES.includes(user?.religion) ? user.religion : 'christian'
  ));
  const [savingReligion, setSavingReligion] = useState(false);
  const [religionMsg, setReligionMsg] = useState(null);
  // "Display only prayers of these religions" — array of religion
  // values selected via checkboxes. Empty = show all (no filter).
  const [displayReligions, setDisplayReligions] = useState(() =>
    Array.isArray(user?.display_religions) ? user.display_religions : []
  );
  // Master toggle for the same-religion feed filter. When OFF, the
  // `displayReligions` checkbox array is ignored server-side and Home
  // feeds show every author regardless of religion. When ON, the
  // checkbox selection below is enforced. Default OFF for new accounts;
  // hydrated from `user.display_religions_filter_enabled` for
  // returning users. Touched ref prevents in-flight refresh() cycles
  // from stomping the local UI state mid-save.
  const [filterEnabled, setFilterEnabled] = useState(
    () => Boolean(user?.display_religions_filter_enabled)
  );
  const filterEnabledTouchedRef = useRef(false);
  useEffect(() => {
    if (filterEnabledTouchedRef.current) return;
    setFilterEnabled(Boolean(user?.display_religions_filter_enabled));
  }, [user?.display_religions_filter_enabled]);
  const [savingFilterEnabled, setSavingFilterEnabled] = useState(false);
  const [filterEnabledMsg, setFilterEnabledMsg] = useState(null);
  const onToggleFilterEnabled = async (checked) => {
    filterEnabledTouchedRef.current = true;
    setFilterEnabled(checked);
    setSavingFilterEnabled(true);
    setFilterEnabledMsg(null);
    try {
      // When flipping to Yes, seed the checkbox list with the user's
      // own profile religion so the filter starts with a relevant
      // selection rather than an empty one. Only auto-adds when the
      // user's religion is a real bucket (i.e. not "none" or
      // "prefer_not_to_say") and isn't already in the current list —
      // respects any pre-existing manual selection the user may have
      // made before turning the filter on.
      let displayReligionsPatch = null;
      if (checked) {
        const own = (user?.religion || '').trim();
        const skip = ['', 'none', 'prefer_not_to_say'];
        if (own && !skip.includes(own)) {
          const current = Array.isArray(displayReligionsRef.current)
            ? displayReligionsRef.current
            : [];
          if (!current.includes(own)) {
            const next = [...current, own];
            displayReligionsRef.current = next;
            setDisplayReligions(next);
            // Mark as touched so a concurrent refresh() doesn't snap
            // the list back before our PATCH lands.
            displayReligionsTouchedRef.current = true;
            displayReligionsPatch = next;
          }
        }
      }
      // Combined PATCH — sending the master toggle plus the seeded
      // list in one round-trip keeps them atomic; a mid-request page
      // reload will never see "filter enabled + list still empty".
      await updateProfile({
        display_religions_filter_enabled: checked,
        ...(displayReligionsPatch !== null ? { display_religions: displayReligionsPatch } : {}),
      });
      setFilterEnabledMsg({ text: 'Saved', tone: 'success' });
      if (typeof refresh === 'function') {
        try {
          const fresh = await refresh();
          if (fresh && 'display_religions_filter_enabled' in fresh) {
            setFilterEnabled(Boolean(fresh.display_religions_filter_enabled));
          }
        } catch (err) { console.warn('refresh after filter toggle failed', err); }
      }
    } catch (err) {
      console.error('save filterEnabled failed', err);
      setFilterEnabled(!checked); // revert on failure
      setFilterEnabledMsg({ text: 'Could not save — try again', tone: 'error' });
    } finally {
      setSavingFilterEnabled(false);
    }
  };

  const [savingDisplayReligions, setSavingDisplayReligions] = useState(false);
  const [displayReligionsMsg, setDisplayReligionsMsg] = useState(null);
  // Once the user has actively toggled a checkbox, ignore incoming
  // `user.display_religions` refreshes — they'd otherwise snap the
  // checkboxes back if any concurrent refresh() cycle races with the
  // save. The next full page load rehydrates from the server normally.
  const displayReligionsTouchedRef = useRef(false);
  // Mirror the array in a ref so click handlers can compute `next`
  // synchronously (state updater callbacks are deferred in React 18's
  // event batching, which caused PATCH bodies to fire with a stale
  // `null` payload).
  const displayReligionsRef = useRef(displayReligions);
  useEffect(() => {
    displayReligionsRef.current = displayReligions;
  }, [displayReligions]);

  // Handle (public @-username). Live availability check throttled via a
  // small debounce timer — we want the "taken" / "available" hint to update
  // as the user types without hammering the API.
  const [handle, setHandle] = useState(() => user?.handle || '');
  const [handleStatus, setHandleStatus] = useState({ state: 'idle', message: '' });
  const handleTimerRef = useRef(null);

  // Avatar
  const fileRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState(null);

  // Password form
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // Re-fetch the profile every time Settings mounts so a reload picks up
  // the current server value even if AuthContext was hydrated a while ago.
  useEffect(() => {
    refresh?.().catch(() => { /* non-critical */ });
  }, [refresh]);

  // Sync local form state whenever the individual persisted fields change on
  // the user object. Watching the specific fields (not the whole `user`
  // reference) avoids spurious runs and guarantees we re-hydrate any time
  // the value truly changed (e.g., updated from another tab).
  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
  }, [user?.name]);
  useEffect(() => {
    if (!user) return;
    setHandle(user.handle || '');
    setHandleStatus({ state: 'idle', message: '' });
  }, [user?.handle]);
  useEffect(() => {
    if (!user) return;
    setZip(user.zip_code || '');
  }, [user?.zip_code]);
  useEffect(() => {
    if (!user) return;
    setBio(user.bio || '');
  }, [user?.bio]);
  useEffect(() => {
    if (!user) return;
    // All four narration modes are now first-class options in the UI.
    // Unknown/future values fall back to `silent` (the product default —
    // no narration).
    const raw = user.narration_preference;
    const next = ['author_only', 'system_fallback', 'silent_highlight', 'silent'].includes(raw) ? raw : 'silent';
    setNarration(next);
    console.info('[Settings narration synced from user]', { serverValue: raw, applied: next });
  }, [user?.narration_preference]);
  useEffect(() => {
    if (!user) return;
    setVoiceGender(user.system_voice_gender || 'female');
  }, [user?.system_voice_gender]);
  useEffect(() => {
    if (!user) return;
    setDefaultTab(user.default_home_tab || 'All');
  }, [user?.default_home_tab]);
  useEffect(() => {
    if (!user) return;
    const val = [10, 25, 50, 100].includes(user.nearby_radius_mi) ? user.nearby_radius_mi : 25;
    setNearbyRadius(val);
  }, [user?.nearby_radius_mi]);
  useEffect(() => {
    if (!user) return;
    setGender(['female', 'male', 'unspecified'].includes(user.gender) ? user.gender : 'unspecified');
  }, [user?.gender]);
  useEffect(() => {
    if (!user) return;
    setReligion(RELIGION_VALUES.includes(user.religion) ? user.religion : 'christian');
  }, [user?.religion, RELIGION_VALUES]);
  useEffect(() => {
    if (!user) return;
    // Skip if the user has already interacted — local state is
    // authoritative until the next page load.
    if (displayReligionsTouchedRef.current) return;
    setDisplayReligions(
      Array.isArray(user.display_religions) ? user.display_religions : []
    );
  }, [user?.display_religions]);

  // Admin-only: site-wide immersive sparks speed
  const [sparksSpeed, setSparksSpeed] = useState(null);
  const [sparksMsg, setSparksMsg] = useState(null);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    getImmersiveSettings()
      .then((d) => setSparksSpeed(d?.sparks_speed ?? 1))
      .catch(() => setSparksSpeed(1));
  }, [user]);

  // ---- Not signed in ----
  if (!authLoading && !user) {
    return (
      <div className="px-4 sm:px-6 py-16 text-center max-w-md mx-auto">
        <UserIcon size={40} className="mx-auto text-amber-500 mb-3" />
        <div className="text-lg font-semibold mb-1">Sign in to edit your profile</div>
        <button
          type="button"
          onClick={openAuthModal}
          data-testid="settings-signin-btn"
          className="mt-4 h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (authLoading || !user) {
    return (
      <div className="py-24 text-center text-zinc-500 flex items-center justify-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading your profile…
      </div>
    );
  }

  const onPickAvatar = () => fileRef.current?.click();

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    // instant local preview
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
    setUploadingAvatar(true);
    try {
      const avatarResp = await uploadAvatar(file);
      await refresh();
      // Fan out the new picture URL to other tabs so their avatars update
      // instantly (Header, Settings preview, participant pill, etc.).
      const newPicture = avatarResp?.picture || avatarResp?.avatar_url || null;
      if (newPicture) broadcastPreferenceUpdate('picture', newPicture);
      setAvatarPreview(null);
      URL.revokeObjectURL(url);
    } catch (err) {
      const sanct = parseSanctuaryError(err);
      if (sanct) {
        setSanctuary(sanct);
      } else {
        setAvatarError(err?.response?.data?.detail || err.message || 'Upload failed');
      }
      setAvatarPreview(null);
      URL.revokeObjectURL(url);
    } finally {
      setUploadingAvatar(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      const trimmedName = name.trim();
      const trimmedZip = zip.trim();
      const trimmedBio = bio.trim();
      const cleanHandle = normalizeHandle(handle);
      const payload = { name: trimmedName, zip_code: trimmedZip, bio: trimmedBio };
      // Only send handle when it changed to avoid tripping the uniqueness
      // check against the caller's own current handle.
      if (cleanHandle !== (user.handle || '')) payload.handle = cleanHandle;
      const saved = await updateProfile(payload);
      await refresh();
      // Broadcast every field individually so other tabs receive granular
      // updates rather than a monolithic profile blob.
      broadcastPreferenceUpdate('name', trimmedName);
      broadcastPreferenceUpdate('zip_code', trimmedZip);
      broadcastPreferenceUpdate('bio', trimmedBio);
      broadcastPreferenceUpdate('handle', saved?.handle || null);
      setProfileMsg({ tone: 'success', text: 'Profile saved' });
    } catch (err) {
      const sanct = parseSanctuaryError(err);
      if (sanct) {
        setSanctuary(sanct);
      } else {
        setProfileMsg({
          tone: 'error',
          text: err?.response?.data?.detail || err.message || 'Could not save profile',
        });
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw !== newPw2) {
      setPwMsg({ tone: 'error', text: 'The two new passwords don\'t match.' });
      return;
    }
    if (newPw.length < 6) {
      setPwMsg({ tone: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    setChangingPw(true);
    try {
      await changePassword(currentPw, newPw);
      setPwMsg({ tone: 'success', text: 'Password updated' });
      setCurrentPw(''); setNewPw(''); setNewPw2('');
    } catch (err) {
      setPwMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not change password',
      });
    } finally {
      setChangingPw(false);
    }
  };

  const onChangeVoiceGender = async (value) => {
    if (value === voiceGender) return;
    setVoiceGender(value);
    setNarrationMsg(null);
    setSavingVoiceGender(true);
    try {
      const saved = await updateProfile({ system_voice_gender: value });
      await refresh();
      broadcastPreferenceUpdate('system_voice_gender', saved?.system_voice_gender || value);
      setNarrationMsg({ tone: 'success', text: `Voice set to ${value === 'female' ? 'Female' : 'Male'}` });
    } catch (err) {
      setNarrationMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not save voice preference',
      });
    } finally {
      setSavingVoiceGender(false);
    }
  };

  const onChangeVoiceId = async (voiceId) => {
    if (voiceId === (user?.system_voice_id || null)) return;
    setNarrationMsg(null);
    setSavingVoiceGender(true);
    try {
      const saved = await updateProfile({ system_voice_id: voiceId });
      await refresh();
      broadcastPreferenceUpdate('system_voice_id', saved?.system_voice_id || voiceId);
      setNarrationMsg({ tone: 'success', text: 'Voice selected' });
    } catch (err) {
      setNarrationMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not save voice',
      });
    } finally {
      setSavingVoiceGender(false);
    }
  };

  // Admin-only handler (hooks live above the early returns).
  const onChangeSparksSpeed = async (v) => {
    setSparksSpeed(v);
    try {
      await updateImmersiveSettings(v);
      setSparksMsg({ text: `Sparks speed set to ${v}× for everyone.`, tone: 'success' });
    } catch {
      setSparksMsg({ text: 'Could not save sparks speed.', tone: 'error' });
    }
    setTimeout(() => setSparksMsg(null), 2500);
  };

  const onChangeNarration = async (value) => {
    console.info('[Settings narration save]', { requested: value });
    setNarration(value);
    setNarrationMsg(null);
    setSavingNarration(true);
    try {
      const saved = await updateProfile({ narration_preference: value });
      console.info('[Settings narration server response]', {
        sent: value,
        savedValue: saved?.narration_preference,
      });
      await refresh();
      // Fan out to other tabs so their WatchPrayer / Settings pages pick
      // up the new pref without a refresh. Send the SERVER-CONFIRMED
      // value, not the requested one, so listeners always see the truth.
      broadcastPreferenceUpdate(
        'narration_preference',
        saved?.narration_preference || value,
      );
      setNarrationMsg({ tone: 'success', text: 'Narration preference saved' });
    } catch (err) {
      console.error('[Settings narration save failed]', err);
      setNarrationMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not save preference',
      });
    } finally {
      setSavingNarration(false);
    }
  };

  const onChangeNearbyRadius = async (value) => {
    if (value === nearbyRadius) return;
    setNearbyRadius(value);
    setRadiusMsg(null);
    setSavingRadius(true);
    try {
      const saved = await updateProfile({ nearby_radius_mi: value });
      await refresh();
      broadcastPreferenceUpdate('nearby_radius_mi', saved?.nearby_radius_mi || value);
      setRadiusMsg({ tone: 'success', text: `Nearby radius set to ${saved?.nearby_radius_mi || value} miles` });
    } catch (err) {
      setRadiusMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not save radius',
      });
    } finally {
      setSavingRadius(false);
    }
  };

  const onChangeGender = async (value) => {
    if (value === gender) return;
    const prev = gender;
    setGender(value);
    setGenderMsg(null);
    setSavingGender(true);
    try {
      const saved = await updateProfile({ gender: value });
      await refresh();
      broadcastPreferenceUpdate('gender', saved?.gender || value);
      const label = value === 'female'
        ? 'Female (she/her)'
        : value === 'male'
          ? 'Male (he/him)'
          : 'Prefer not to say (name only)';
      setGenderMsg({ tone: 'success', text: `Gender set to ${label}` });
    } catch (err) {
      // Roll back the optimistic update on failure.
      setGender(prev);
      setGenderMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not save gender',
      });
    } finally {
      setSavingGender(false);
    }
  };

  const onChangeReligion = async (value) => {
    if (value === religion) return;
    const prev = religion;
    setReligion(value);
    setReligionMsg(null);
    setSavingReligion(true);
    try {
      const saved = await updateProfile({ religion: value });
      await refresh();
      broadcastPreferenceUpdate('religion', saved?.religion || value);
      const label = (RELIGION_OPTIONS.find((o) => o.value === value) || {}).label || value;
      setReligionMsg({ tone: 'success', text: `Faith tradition set to ${label}` });
    } catch (err) {
      // Roll back the optimistic update on failure.
      setReligion(prev);
      setReligionMsg({
        tone: 'error',
        text: err?.response?.data?.detail || err.message || 'Could not save faith tradition',
      });
    } finally {
      setSavingReligion(false);
    }
  };

  const onToggleDisplayReligion = (value, checked) => {
    // Mark that local state is now authoritative. The hydration effect
    // will skip further syncs from `user.display_religions` refreshes.
    displayReligionsTouchedRef.current = true;
    // Compute `next` from the ref (synchronous, always latest) so
    // consecutive clicks don't clobber each other and the PATCH body
    // is always populated with the freshest array.
    const prev = displayReligionsRef.current;
    const nextSet = new Set(prev);
    if (checked) nextSet.add(value); else nextSet.delete(value);
    // Preserve declaration order from RELIGION_OPTIONS so the stored
    // list has a stable ordering for future UI reads.
    const next = RELIGION_OPTIONS.map((o) => o.value).filter((v) => nextSet.has(v));
    displayReligionsRef.current = next;
    setDisplayReligions(next);
    setDisplayReligionsMsg(null);
    setSavingDisplayReligions(true);
    (async () => {
      try {
        await updateProfile({ display_religions: next });
        // Skip AuthContext refresh here — the effect that syncs
        // `displayReligions` from `user.display_religions` fires on
        // every refresh() and could snap the checkbox back mid-typing
        // if the user has clicked another box in the meantime. The
        // list is already correct locally; the next natural refresh
        // (page load or explicit action) reconciles.
        setDisplayReligionsMsg({
          tone: 'success',
          text: next.length === 0
            ? 'Showing prayers from all religions.'
            : `Filtering to ${next.length} religion${next.length === 1 ? '' : 's'}.`,
        });
      } catch (err) {
        // Roll back to whatever the server currently believes.
        try {
          const fresh = await refresh();
          if (Array.isArray(fresh?.display_religions)) {
            displayReligionsRef.current = fresh.display_religions;
            setDisplayReligions(fresh.display_religions);
          }
        } catch (err) { console.warn('Settings: rollback refresh after preference save failure failed', err); }
        setDisplayReligionsMsg({
          tone: 'error',
          text: err?.response?.data?.detail || err.message || 'Could not save preference',
        });
      } finally {
        setSavingDisplayReligions(false);
      }
    })();
  };

  const profileDirty =
    (name.trim() !== (user.name || '')) ||
    ((zip.trim() || null) !== (user.zip_code || null)) ||
    ((bio.trim() || null) !== (user.bio || null)) ||
    (normalizeHandle(handle) !== (user.handle || ''));

  const showProfile = view !== 'settings';
  const showSettings = view !== 'profile';
  const pageTitle = view === 'profile'
    ? 'Profile'
    : view === 'settings'
      ? 'Settings'
      : 'Settings';
  const pageSubtitle = view === 'profile'
    ? 'How you appear across Pray Now Together.'
    : view === 'settings'
      ? 'Your app preferences on this device and account.'
      : 'Update how you appear on Pray Now Together.';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="settings-page">
      <SanctuaryModerationDialog
        open={Boolean(sanctuary)}
        variant={sanctuary?.variant}
        reason={sanctuary?.reason}
        source={sanctuary?.source}
        onClose={() => setSanctuary(null)}
      />
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{pageTitle}</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{pageSubtitle}</p>
        </div>
      </header>

      {/* Profile */}
      {showProfile && (
      <Section title="Profile" subtitle="Your name and photo show up next to every prayer you share.">
        <div className="flex items-center gap-5 mb-6">
          <div className="relative w-20 h-20 rounded-full overflow-hidden ring-1 ring-zinc-200 dark:ring-zinc-800 flex-shrink-0">
            <Avatar user={user} previewUrl={avatarPreview} />
            {uploadingAvatar && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Loader2 className="animate-spin text-white" size={22} />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onAvatarFile} className="hidden" data-testid="settings-avatar-input" />
            <button
              type="button"
              onClick={onPickAvatar}
              disabled={uploadingAvatar}
              data-testid="settings-avatar-btn"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              <Camera size={16} /> Change photo
            </button>
            <div className="text-xs text-zinc-500 mt-1">JPG, PNG, WEBP, or GIF · up to 4 MB</div>
            {avatarError && <div className="text-xs text-red-600 mt-1" data-testid="settings-avatar-error">{avatarError}</div>}
          </div>
        </div>

        <form onSubmit={onSaveProfile} className="space-y-4">
          <Field label="Display name">
            <InputRow
              icon={UserIcon}
              value={name}
              onChange={(e) => setName(e.target.value)}
              type="text"
              maxLength={80}
              placeholder="Your name"
              data-testid="settings-name-input"
            />
          </Field>

          <Field
            label="Handle"
            hint={
              handleStatus.state === 'checking'
                ? 'Checking availability…'
                : handleStatus.state === 'taken'
                  ? handleStatus.message
                  : handleStatus.state === 'invalid'
                    ? handleStatus.message
                    : handleStatus.state === 'available'
                      ? handleStatus.message
                      : "3-30 characters, letters/numbers/underscore. Shown as @yourhandle on your profile."
            }
          >
            <div className={`flex items-center h-11 rounded-xl border bg-white dark:bg-zinc-950 focus-within:border-amber-500 dark:focus-within:border-amber-400 overflow-hidden ${
              handleStatus.state === 'taken' || handleStatus.state === 'invalid'
                ? 'border-red-400 dark:border-red-700'
                : handleStatus.state === 'available'
                  ? 'border-emerald-400 dark:border-emerald-700'
                  : 'border-zinc-300 dark:border-zinc-700'
            }`}>
              <AT_ICON className="ml-3 text-zinc-400 flex-shrink-0" />
              <input
                data-testid="settings-handle-input"
                value={handle}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Live-normalize so the input never shows illegal characters.
                  const clean = normalizeHandle(raw);
                  setHandle(clean);
                  // Debounced availability check
                  if (handleTimerRef.current) clearTimeout(handleTimerRef.current);
                  if (!clean) {
                    setHandleStatus({ state: 'idle', message: '' });
                    return;
                  }
                  if (clean === (user.handle || '')) {
                    setHandleStatus({ state: 'idle', message: '' });
                    return;
                  }
                  if (!HANDLE_RE.test(clean)) {
                    setHandleStatus({ state: 'invalid', message: 'Handle must be 3-30 characters.' });
                    return;
                  }
                  setHandleStatus({ state: 'checking', message: '' });
                  handleTimerRef.current = setTimeout(async () => {
                    try {
                      const resp = await checkHandleAvailable(clean);
                      if (resp?.available) {
                        setHandleStatus({ state: 'available', message: `@${clean} is available` });
                      } else {
                        // `reason` narrows the client-side message.
                        // Server may return: 'invalid' (format),
                        // 'reserved' (on the reserved list), or
                        // 'taken' (already claimed).
                        let message;
                        if (resp?.reason === 'invalid') {
                          message = 'Handle must be 3-30 characters.';
                        } else if (resp?.reason === 'reserved') {
                          message = 'That handle is currently unavailable.';
                        } else {
                          message = 'That handle is already taken.';
                        }
                        setHandleStatus({
                          state: resp?.reason === 'invalid' ? 'invalid' : 'taken',
                          message,
                        });
                      }
                    } catch (_) {
                      setHandleStatus({ state: 'idle', message: '' });
                    }
                  }, 350);
                }}
                type="text"
                maxLength={30}
                placeholder="yourhandle"
                className="flex-1 bg-transparent outline-none px-2 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400 lowercase"
              />
              {handleStatus.state === 'checking' && (
                <Loader2 size={16} className="animate-spin text-zinc-400 mr-3" data-testid="settings-handle-checking" />
              )}
              {handleStatus.state === 'available' && (
                <Check size={16} className="text-emerald-500 mr-3" data-testid="settings-handle-available" />
              )}
            </div>
          </Field>

          <Field label="Email" hint="Email can't be changed right now — contact support if you need help.">
            <InputRow icon={Mail} value={user.email} disabled type="email" data-testid="settings-email-input" />
          </Field>

          <Field label="Zip code" hint="Optional — used for those nearby to participate with your prayer.">
            <InputRow
              icon={MapPin}
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              type="text"
              maxLength={16}
              placeholder="e.g. 94103"
              data-testid="settings-zip-input"
            />
          </Field>

          <Field
            label="About you"
            hint={`Optional — a short intro shown on your Circle page. ${Math.max(0, 280 - bio.length)} left.`}
          >
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 280))}
              maxLength={280}
              rows={3}
              placeholder="e.g. - a mission statement, a favorite quote, a welcome message"
              data-testid="settings-bio-input"
              className="w-full rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 focus:border-amber-500 dark:focus:border-amber-400 outline-none px-3 py-2 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400 resize-y"
            />
          </Field>

          <Field
            label="Gender"
            hint="Used only by the Prayer assistance to pick natural pronouns when it writes prayers about you."
          >
            <div className="flex flex-wrap gap-2" data-testid="settings-gender-picker">
              {[
                { value: 'female', label: 'Female', sub: 'she / her' },
                { value: 'male', label: 'Male', sub: 'he / him' },
                { value: 'unspecified', label: 'Prefer not to say', sub: 'name only' },
              ].map((opt) => {
                const selected = gender === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChangeGender(opt.value)}
                    disabled={savingGender}
                    data-testid={`settings-gender-${opt.value}`}
                    aria-pressed={selected}
                    className={`inline-flex flex-col items-start gap-0.5 px-3.5 py-2 rounded-xl border text-sm transition-colors ${
                      selected
                        ? 'bg-amber-500 border-amber-500 text-white'
                        : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                    } ${savingGender ? 'opacity-60 cursor-wait' : ''}`}
                  >
                    <span className="font-medium leading-tight">{opt.label}</span>
                    <span className={`text-[11px] ${selected ? 'text-white/80' : 'text-zinc-500'}`}>{opt.sub}</span>
                  </button>
                );
              })}
            </div>
            {genderMsg && <div className="mt-2"><Toast text={genderMsg.text} tone={genderMsg.tone} /></div>}
          </Field>

          {SHOW_RELIGION_UI && (
          <>
          <Field
            label="Faith tradition"
            hint="Optional — steers the Prayer Assistance so drafts match your voice. Change any time."
          >
            <select
              value={religion}
              onChange={(e) => onChangeReligion(e.target.value)}
              disabled={savingReligion}
              data-testid="settings-religion-select"
              className={`w-full h-11 px-3 rounded-xl border text-sm bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-amber-400/60 ${
                savingReligion ? 'opacity-60 cursor-wait' : ''
              }`}
            >
              {RELIGION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} data-testid={`settings-religion-option-${opt.value}`}>
                  {opt.label}
                </option>
              ))}
            </select>
            {religionMsg && <div className="mt-2"><Toast text={religionMsg.text} tone={religionMsg.tone} /></div>}
          </Field>
          </>
          )}
          {profileMsg && <Toast text={profileMsg.text} tone={profileMsg.tone} />}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="submit"
              disabled={savingProfile || !profileDirty}
              data-testid="settings-save-profile-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold"
            >
              {savingProfile ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save changes
            </button>
            {!profileDirty && !savingProfile && <span className="text-xs text-zinc-400 inline-flex items-center gap-1"><Check size={12} /> All set</span>}
          </div>
        </form>
      </Section>
      )}

      {/* Display */}
      {showSettings && (
      <>
      <Section
        title="Display"
        subtitle="Choose how Pray Now Together looks. Your preference is saved on this device."
      >
        <div className="grid grid-cols-2 gap-3" data-testid="display-options">
          {[
            { value: 'light', title: 'Light', desc: 'Bright and airy.',   icon: Sun  },
            { value: 'dark',  title: 'Dark',  desc: 'Softer on the eyes.', icon: Moon },
          ].map((opt) => {
            const active = theme === opt.value;
            const Icon = opt.icon;
            return (
              <label
                key={opt.value}
                data-testid={`display-option-${opt.value}`}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  active
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500/60'
                    : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
                }`}
              >
                <input
                  type="radio"
                  name="theme-preference"
                  value={opt.value}
                  checked={active}
                  onChange={() => setTheme(opt.value)}
                  className="sr-only"
                />
                <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  active ? 'border-amber-500 bg-amber-500' : 'border-zinc-400 dark:border-zinc-500'
                }`}>
                  {active && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                <Icon size={20} className={active ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-500'} />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{opt.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </Section>

      {/* Immersive sparks speed — admin-only, site-wide */}
      {user?.role === 'admin' && (
      <Section
        title="Immersive Sparks (Admin)"
        subtitle="Site-wide speed of the floating sparks in the immersive watch. Applies to every viewer."
      >
        <div className="flex flex-wrap gap-2" data-testid="sparks-speed-options">
          {[0.5, 0.75, 1, 1.5, 2, 3].map((v) => {
            const active = sparksSpeed === v;
            return (
              <button
                key={v}
                type="button"
                data-testid={`sparks-speed-${String(v).replace('.', '_')}`}
                onClick={() => onChangeSparksSpeed(v)}
                disabled={sparksSpeed === null}
                className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-500/60'
                    : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
                }`}
              >
                {v}×
              </button>
            );
          })}
        </div>
        {sparksMsg && <div className="mt-2"><Toast text={sparksMsg.text} tone={sparksMsg.tone} /></div>}
      </Section>
      )}

      {/* Narration preference */}
      <Section
        title="Narration"
        subtitle="Choose how prayers should be played when you open them."
      >
        <div className="space-y-2" data-testid="narration-options">
          {[
            {
              value: 'author_only',
              icon: Mic,
              title: "Only owner's voice",
              desc: "Play only when the owner has recorded their prayer — otherwise stay silent.",
            },
            {
              value: 'system_fallback',
              icon: Speaker,
              title: 'System narration if no recording',
              desc: "Play the owner's voice when available; fall back to a system-generated voice otherwise.",
            },
            {
              value: 'silent_highlight',
              icon: Highlighter,
              title: 'Do not play any narration - use highlighting',
              desc: 'Silent playback with synchronized word-by-word highlighting to guide your prayer visually.',
            },
            {
              value: 'silent',
              icon: VolumeX,
              title: 'Do not play any narration',
              desc: 'Always a silent prayer — read at your own pace.',
            },
          ].map((opt) => {
            const active = narration === opt.value;
            const Icon = opt.icon;
            return (
              <label
                key={opt.value}
                data-testid={`narration-option-${opt.value}`}
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  active
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500/60'
                    : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
                }`}
              >
                <input
                  type="radio"
                  name="narration-preference"
                  value={opt.value}
                  checked={active}
                  onChange={() => onChangeNarration(opt.value)}
                  disabled={savingNarration}
                  className="sr-only"
                />
                <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  active ? 'border-amber-500 bg-amber-500' : 'border-zinc-400 dark:border-zinc-500'
                }`}>
                  {active && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                <Icon size={18} className={`mt-0.5 flex-shrink-0 ${active ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-500'}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{opt.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
                  {opt.value === 'system_fallback' && active && (
                    <VoiceGenderPicker
                      current={voiceGender}
                      currentVoiceId={user?.system_voice_id || null}
                      saving={savingVoiceGender}
                      onChangeGender={onChangeVoiceGender}
                      onChangeVoiceId={onChangeVoiceId}
                    />
                  )}
                </div>
              </label>
            );
          })}
        </div>
        {narrationMsg && <div className="mt-3"><Toast text={narrationMsg.text} tone={narrationMsg.tone} /></div>}
      </Section>

      {/* Nearby radius */}
      <Section
        title="Nearby radius"
        subtitle="Choose the distance we search when showing prayers on the Nearby tab. Uses your zip code (Profile section) to measure distance."
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="nearby-radius-options">
          {[10, 25, 50, 100].map((miles) => {
            const active = nearbyRadius === miles;
            return (
              <label
                key={miles}
                data-testid={`nearby-radius-option-${miles}`}
                className={`flex flex-col items-center justify-center gap-1 p-3 rounded-xl border cursor-pointer transition-colors ${
                  active
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500/60'
                    : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
                }`}
              >
                <input
                  type="radio"
                  name="nearby-radius"
                  value={miles}
                  checked={active}
                  onChange={() => onChangeNearbyRadius(miles)}
                  disabled={savingRadius}
                  className="sr-only"
                />
                <div className={`text-lg font-semibold ${active ? 'text-amber-600 dark:text-amber-300' : 'text-zinc-800 dark:text-zinc-100'}`}>
                  {miles} mi
                </div>
                <div className="text-xs text-zinc-500">
                  {miles === 10 && 'Neighborhood'}
                  {miles === 25 && 'Default'}
                  {miles === 50 && 'Nearby cities'}
                  {miles === 100 && 'Wider region'}
                </div>
              </label>
            );
          })}
        </div>
        {radiusMsg && <div className="mt-3"><Toast text={radiusMsg.text} tone={radiusMsg.tone} /></div>}
      </Section>

      {/* My Circle page — one-toggle section for the stats grid.
          Off by default so authors don't feel exposed by numbers on
          their own profile until they explicitly opt in. When ON,
          the setting is PUBLIC — every visitor to /circle/<user_id>
          sees the member count + 3-card stats grid. */}
      <Section
        title="My Circle page"
        subtitle="Choose what shows up on your public Circle page."
      >
        <label
          data-testid="circle-stats-pref"
          className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
            showCircleStats
              ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500/60'
              : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
          } ${savingCircleStats ? 'opacity-70 cursor-wait' : ''}`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={showCircleStats}
            disabled={savingCircleStats}
            onChange={(e) => onToggleCircleStats(e.target.checked)}
            data-testid="circle-stats-pref-input"
          />
          <span
            className={`mt-0.5 relative inline-flex items-center flex-shrink-0 h-5 w-9 rounded-full transition-colors ${
              showCircleStats ? 'bg-amber-500' : 'bg-zinc-300 dark:bg-zinc-700'
            }`}
            aria-hidden="true"
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                showCircleStats ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Show stats on my Circle page</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              When on, visitors to your Circle page see your member count, total prayers, total times prayed, and follower count. Off keeps those numbers private.
            </div>
          </div>
          {savingCircleStats && <Loader2 size={14} className="animate-spin text-zinc-400 ml-auto mt-1" data-testid="circle-stats-pref-saving" />}
        </label>
        {circleStatsMsg && <div className="mt-3"><Toast text={circleStatsMsg.text} tone={circleStatsMsg.tone} /></div>}
      </Section>

      {/* Notifications — per-event toggles for in-app notifications.
          Backend gates writes in `create_direct_notification` /
          `notify_prayer_watched` so a disabled type simply never
          lands in the recipient's inbox. Admin messages are always
          delivered and therefore not exposed here. */}
      <Section
        title="Notifications"
        subtitle="Choose which in-app notifications land in your bell. Admin messages are always delivered."
      >
        <div className="space-y-2" data-testid="notification-prefs">
          {[
            {
              key: 'inapp_prayer_watched',
              title: 'When someone prays for your prayer',
              desc: 'You get a bell notification whenever a viewer opens and prays your prayer.',
            },
            {
              key: 'inapp_random_share',
              title: 'When a random prayer is shared with you',
              desc: 'You get notified when another author asks the community to pray, and you\'re picked.',
            },
            {
              key: 'inapp_thanks_reply',
              title: '"Thank you for praying" replies',
              desc: 'When the author of a prayer you prayed for sends you a thank-you note back.',
            },
          ].map((row) => {
            const on = Boolean(notifPrefs[row.key]);
            const saving = savingNotif === row.key;
            return (
              <label
                key={row.key}
                data-testid={`notif-pref-${row.key}`}
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  on
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500/60'
                    : 'border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
                } ${saving ? 'opacity-70 cursor-wait' : ''}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={on}
                  disabled={saving}
                  onChange={(e) => onToggleNotif(row.key, e.target.checked)}
                  data-testid={`notif-pref-${row.key}-input`}
                />
                <span
                  className={`mt-0.5 relative inline-flex items-center flex-shrink-0 h-5 w-9 rounded-full transition-colors ${
                    on ? 'bg-amber-500' : 'bg-zinc-300 dark:bg-zinc-700'
                  }`}
                  aria-hidden="true"
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                      on ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </span>
                <Bell size={18} className={`mt-0.5 flex-shrink-0 ${on ? 'text-amber-600 dark:text-amber-400' : 'text-zinc-500'}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{row.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{row.desc}</div>
                </div>
                {saving && <Loader2 size={14} className="animate-spin text-zinc-400 ml-auto mt-1" data-testid={`notif-pref-${row.key}-saving`} />}
              </label>
            );
          })}
        </div>
        {notifMsg && <div className="mt-3"><Toast text={notifMsg.text} tone={notifMsg.tone} /></div>}
      </Section>
      </>
      )}

      {/* Security */}
      {showProfile && (
      <Section title="Password" subtitle="Choose something you'll remember — at least 6 characters.">
        <form onSubmit={onChangePassword} className="space-y-4">
          <Field label="Current password">
            <InputRow
              icon={Lock}
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              type="password"
              placeholder="Your current password"
              autoComplete="current-password"
              data-testid="settings-current-pw"
            />
          </Field>
          <Field label="New password">
            <InputRow
              icon={Lock}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              type="password"
              placeholder="At least 6 characters"
              autoComplete="new-password"
              data-testid="settings-new-pw"
            />
          </Field>
          <Field label="Confirm new password">
            <InputRow
              icon={Lock}
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              type="password"
              placeholder="Repeat the new password"
              autoComplete="new-password"
              data-testid="settings-new-pw2"
            />
          </Field>

          {pwMsg && <Toast text={pwMsg.text} tone={pwMsg.tone} />}

          <button
            type="submit"
            disabled={changingPw || !currentPw || !newPw || !newPw2}
            data-testid="settings-change-pw-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-semibold"
          >
            {changingPw ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            Update password
          </button>
        </form>
      </Section>
      )}
    </div>
  );
};

export default Settings;
